import { getRestBaseUrl, buildAuthHeaders } from '../bridges/box-bridge';

/**
 * Thrown for any non-2xx response from the v3 REST API.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResponse<T> {
  data: T;
  headers: Headers;
}

function buildHeaders(init?: HeadersInit): Record<string, string> {
  return {
    ...buildAuthHeaders(),
    ...(init as Record<string, string> | undefined),
  };
}

/**
 * Authenticated fetch against the v3 REST API (TL.REST_URL), returning the parsed JSON body
 * alongside the raw response headers (needed e.g. for the "hasMore" paging header).
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
  const response = await fetch(getRestBaseUrl() + path, {
    ...init,
    headers: buildHeaders(init.headers),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => undefined);
    throw new ApiError(`Request to ${path} failed with status ${response.status}`, response.status, body);
  }

  const data = (await response.json()) as T;
  return { data, headers: response.headers };
}

/**
 * Authenticated fetch returning the raw Response, for endpoints whose payload isn't JSON
 * (e.g. a file download) - callers read headers/blob themselves.
 */
export async function apiFetchRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(getRestBaseUrl() + path, {
    ...init,
    headers: buildHeaders(init.headers),
  });

  if (!response.ok) {
    throw new ApiError(`Request to ${path} failed with status ${response.status}`, response.status);
  }

  return response;
}

/**
 * Wraps a fetch-issuing function so that only its most recent call ever settles - any
 * earlier still-in-flight call from the same wrapped function is aborted (via the
 * `AbortSignal` passed to `fn`) and its returned promise is left permanently pending
 * rather than rejecting with an AbortError.
 *
 * This is what "cancel the previous request" means at the call-site level: a rapid
 * double-click (e.g. toggling a sort column) would otherwise leave both requests racing,
 * wasting a server round-trip and risking the stale one's `.then()`/`.catch()` clearing
 * UI state (like a loading spinner) that the newer, still-pending request still needs.
 * Leaving the stale promise pending - instead of resolving/rejecting it - means existing
 * `.then()`/`.catch()` callers need no special-case abort handling to be safe.
 *
 * Use for list/search endpoints where a newer call always supersedes an older one -
 * not for independent one-off actions (e.g. a user-triggered download) that should
 * always complete regardless of what else is called afterwards.
 */
export function latestOnly<Args extends unknown[], T>(
  fn: (signal: AbortSignal, ...args: Args) => Promise<T>
): (...args: Args) => Promise<T> {
  let currentController: AbortController | undefined;

  return (...args: Args) =>
    new Promise<T>((resolve, reject) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;

      fn(controller.signal, ...args).then(
        (value) => {
          if (!controller.signal.aborted) {
            resolve(value);
          }
        },
        (error) => {
          if (!controller.signal.aborted) {
            reject(error);
          }
        }
      );
    });
}
